import React, { FC, useMemo } from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Box,
  FormHelperText
} from '@mui/material';
import { Client } from 'axiosApi/models';
import intl from 'components/i18n/ReactIntlWrapper';
import { sortCollection } from '../../../../common/utils';
import { SORT_TYPES } from '../../../../common/constants';
import { Visibility } from '@mui/icons-material';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { ROLES } from 'common/permissions';
import { useNavigate } from 'react-router';
import Spinner from 'components/Spinner';

type ClientSelectProps = {
  handleClientChange: (client: Client | null, id: any) => void;
  selectedClient: string | null;
  clients: Client[] | null | undefined;
  disabled?: boolean;
  newUser?: boolean;
  id?: any;
  setHiddenRole?: (hidden: boolean) => void;
  enableEdit?: boolean;
  errors?: any;
};

const ClientSelect: FC<ClientSelectProps> = ({
  handleClientChange,
  clients,
  selectedClient,
  disabled = false,
  newUser = false,
  id = null,
  setHiddenRole,
  enableEdit = false,
  errors
}) => {
  const navigate = useNavigate();

  const isLoading = clients === null || clients === undefined;

  const clientsSorted = useMemo(
    () => (clients ? sortCollection(clients, 'name', SORT_TYPES.ASC) : []),
    [clients]
  );

  const selectedClientObj = useMemo(
    () => clients?.find((c) => c.id === Number(selectedClient)) || null,
    [clients, selectedClient]
  );

  const handleChange = (event: SelectChangeEvent<string>) => {
    const clientId = parseInt(event.target.value);
    const selected = clients?.find((client) => client.id === clientId) || null;
    handleClientChange(selected, id);
  };

  const handleClientDetail = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/client/detail/' + selectedClient);
  };

  const labelText = "* " + intl.get('projectForm.placeholder.client');

  return (
    <FormControl fullWidth error={!!errors.idClient}>
      <InputLabel
        id="client-select-label"
        sx={{
          '&:not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 10px) scale(1)',
          }
        }}
      >
        {labelText}
      </InputLabel>
      <Select
        labelId="client-select-label"
        id="idClient"
        name="idClient"
        value={selectedClient}
        label={labelText}
        error={!!errors.idClient}
        onChange={handleChange}
        disabled={disabled || isLoading}
        onOpen={() => setHiddenRole?.(true)}
        onClose={() => setHiddenRole?.(false)}
        renderValue={(value) => {
          if (isLoading) {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Spinner />
              </Box>
            );
          }

          if (!selectedClientObj) {
            return null;
          }

          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>{selectedClientObj.name}</Typography>
              {enableEdit && disabled && (
                <PermissionsGate
                  identifier="ui.projects.form.client-select"
                  viewRoles={[ROLES.projectReader]}
                  editRoles={[ROLES.projectReader]}
                >
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClientDetail}
                    sx={{
                      minWidth: 'auto',
                      maxHeight: 20,
                      padding: 0.5,
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <Visibility fontSize="small" />
                  </Button>
                </PermissionsGate>
              )}
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: newUser ? 299 : undefined
            },
          },
        }}
      >
        {clientsSorted.map((client: Client) => (
          <MenuItem key={client.id} value={client.id}>
            {client.name}
          </MenuItem>
        ))}
      </Select>
      {errors.idClient && <FormHelperText>{errors.idClient}</FormHelperText>}
    </FormControl>
  );
};

export default ClientSelect;
